<template>
  <div>
    <Layout :containers="containers">
      <template v-slot:header>
        <DefaultHeroBar
          :hasQuickStartCallout="false"
          :section="channel.heroSection"
        />
      </template>

      <template v-slot:intro>
        <AdviserEnrolmentIntro
          :heading="channel.intro.heading"
          :lead="channel.intro.lead"
          :paragraphs="channel.intro.paragraphs"
          :footnote="channel.intro.footnote"
          paragraphSize="body"
          flush
        />
        <HR narrow />
      </template>

      <template v-slot:earnings>
        <AdviserEnrolmentAccentuatedContent
          :heading="channel.earnings.heading"
          :intro="channel.earnings.intro"
          :bullets="channel.earnings.bullets"
          :outro="channel.earnings.outro"
          web
        />
      </template>

      <template v-slot:registration>
        <AdviserEnrolmentContent
          :allowedCompanyIds="channel.companyIds"
          :faqCategoriesOverride="[]"
          :termsOverride="[]"
          :showFaqs="false"
          :showFaqsBanner="false"
          :showWhatsNewBanner="false"
          :showCustomerBrochureBanner="false"
          cardStyle="web"
          @showErrorMessage="showErrorMessage"
        />
        <AdviserChannelSelector :currentChannel="channel.key" />
      </template>

      <template v-slot:incentives>
        <section class="adviser-section">
          <AdviserSectionHeading
            :title="channel.incentives.heading"
            variant="b"
          />
          <p class="incentives-lead">
            <strong>{{ channel.incentives.lead }}</strong>
          </p>
          <p class="incentives-body">{{ channel.incentives.body }}</p>
        </section>
        <HR narrow />
      </template>

      <template v-slot:resources>
        <section class="adviser-section">
          <AdviserSectionHeading
            :title="channel.resources.heading"
            :subtitle="channel.resources.subtitle"
          />
          <AdviserBenefitCardGrid class="cards" :cards="channel.brochures" />
        </section>
        <HR narrow />
      </template>

      <template v-slot:linkCards>
        <section class="adviser-section">
          <AdviserBenefitCardGrid :cards="linkCards" />
        </section>
      </template>

      <template v-slot:terms>
        <AdviserTermsSection v-if="channel.terms" :terms="channel.terms" />
      </template>
    </Layout>
    <ErrorSnackbar v-model="showError" :error="errorMessage" />
  </div>
</template>

<script>
  import { CHANNEL_LINK_CARDS } from '@/constants/adviser-enrolment/adviser-content';
  import ErrorSnackbar from '@/components/common/ErrorSnackbar.vue';
  import Layout from '../../common/layouts/Layout.vue';
  import DefaultHeroBar from '../../common/DefaultHeroBar.vue';
  import HR from '../../common/HR.vue';
  import AdviserEnrolmentIntro from './AdviserEnrolmentIntro.vue';
  import AdviserEnrolmentAccentuatedContent from './AdviserEnrolmentAccentuatedContent.vue';
  import AdviserEnrolmentContent from './AdviserEnrolmentContent.vue';
  import AdviserChannelSelector from './AdviserChannelSelector.vue';
  import AdviserSectionHeading from './AdviserSectionHeading.vue';
  import AdviserBenefitCardGrid from './AdviserBenefitCardGrid.vue';
  import AdviserTermsSection from './AdviserTermsSection.vue';

  // Shared layout for the adviser channel pages (/ifa, /pf). Everything
  // channel-specific comes from the `channel` config object; see
  // constants/adviser-enrolment/ifa-content.js for the shape.
  export default {
    name: 'AdviserChannelPage',
    components: {
      ErrorSnackbar,
      Layout,
      DefaultHeroBar,
      HR,
      AdviserEnrolmentIntro,
      AdviserEnrolmentAccentuatedContent,
      AdviserEnrolmentContent,
      AdviserChannelSelector,
      AdviserSectionHeading,
      AdviserBenefitCardGrid,
      AdviserTermsSection,
    },
    props: {
      channel: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        linkCards: CHANNEL_LINK_CARDS,
        errorMessage: null,
        showError: false,
      };
    },
    methods: {
      showErrorMessage(errorMessage) {
        this.errorMessage = errorMessage;
        this.showError = true;
      },
    },
    computed: {
      containers() {
        return [
          {
            name: 'header',
            background: 'countryPattern',
            class: 'hero-banner',
          },
          {
            name: 'intro',
            background: 'countryPattern',
            fadedRow: true,
          },
          // 88px under the intro divider, matching the 88px above it.
          { name: 'earnings', class: 'section-padding' },
          { name: 'registration' },
          { name: 'incentives' },
          { name: 'resources' },
          { name: 'linkCards' },
          { name: 'terms' },
        ];
      },
    },
  };
</script>

<style scoped lang="scss">
  @import '../../../constants/styles/adviser-pages.scss';

  .adviser-section {
    @include adviser-content-column;
    margin-top: $adviser-section-gap;

    .cards {
      margin-top: 60px;
    }
  }

  .incentives-lead,
  .incentives-body {
    @include adviser-body;
    margin: 0;
  }
  .incentives-lead {
    margin-top: 30px;
    margin-bottom: 24px;
  }

  // The channel selector sits 88px under the form's contact line.
  ::v-deep .channel-selector-wrapper {
    margin-top: $adviser-section-gap;
  }
</style>
